import styled from 'styled-components';
import { QRCodeCanvas } from 'qrcode.react';
import { useFormContext } from 'react-hook-form';

import { SITE_DELIVERY_TYPES_LABELS, SITE_TYPES_LABELS } from 'pages/Sites/constants/types';
import { SITE_STATES_LABELS } from 'pages/Sites/constants/states';
import {
    SiteContractType,
    SiteDeliveryType,
    SiteFragmentFragment,
    SiteState,
    SiteType,
    useGetSiteMetabaseDashboardUrlQuery,
} from 'data/__generated__';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { TotemLabel } from 'components/TotemLabel';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { DetailFormCheckbox, DetailFormDatePicker, DetailFormValue } from 'components/DetailsView';
import { CopyValue } from 'components/CopyValue';
import { StatusTag } from 'pages/Sites/components/StatusTag';
import { OrganizationSelect } from 'components/Select/OrganizationSelect';

import { dateFromString, formatDateAsAnniversary, getExtendedFormattedDatetime } from '../../../helpers/dateTimes';
import { TotemImage } from 'components/TotemImage';

const QRCODE_BASE_URL = 'https://onelink.to/sgv8qn?siteId=';

function getAvaLink(avaClientId?: number | null, avaSiteId?: number | null): string {
    return 'https://neocorner.app.ava-vending.fr/index.html#/customers/' + avaClientId + '/customerSites/' + avaSiteId;
}

export const GeneralInfoSection = ({ siteDetails }: { siteDetails: SiteFragmentFragment }) => {
    const { register, setValue, control, watch } = useFormContext();
    const {
        _id: siteId,
        unknownShrinkageBillingThreshold,
        createdAt,
        deliveryType,
        estimatedUserCount,
        hasExternalSetupProcess,
        isOpenOnWeekend,
        isSecured,
        hasSensei,
        hasCoffee,
        launchDate,
        name,
        removalDate,
        shouldOperateOnTotops,
        shouldAutomaticallyCreateTargetReassortment,
        shouldAutomaticallyCreateManualReassortment,
        shouldAutomaticallyFinishSiteSetup,
        shouldHaveTheftNotice,
        shouldModulateForExpired,
        shouldModulateForUnknownLosses,
        state,
        stateHistory,
        totemNumber,
        type,
        useNayaxCard,
        updatedAt,
        avaSiteId,
        avaClientId,
        contractType,
    } = siteDetails;

    const watchedOrganizationId = watch('organizationId');

    const { data: metabaseDashboardData, error: metabaseDashboardError } = useGetSiteMetabaseDashboardUrlQuery({
        variables: { siteId },
    });

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const qrCodeUrl = `${QRCODE_BASE_URL}${siteId}`;

    const stateOptions = [
        { value: SiteState.Regular, label: SITE_STATES_LABELS.Regular },
        { value: SiteState.Archived, label: SITE_STATES_LABELS.Archived },
    ];
    const typesOptions = [
        { value: SiteType.Microstore, label: SITE_TYPES_LABELS.Microstore },
        { value: SiteType.Warehouse, label: SITE_TYPES_LABELS.Warehouse },
        { value: SiteType.BrokenProductsWarehouse, label: SITE_TYPES_LABELS.BrokenProductsWarehouse },
        { value: SiteType.ExpiredProductsWarehouse, label: SITE_TYPES_LABELS.ExpiredProductsWarehouse },
    ];

    const deliveryTypesOptions = [
        { value: SiteDeliveryType.MicrostoreDelivery, label: SITE_DELIVERY_TYPES_LABELS.MicrostoreDelivery },
        { value: SiteDeliveryType.WarehouseDelivery, label: SITE_DELIVERY_TYPES_LABELS.WarehouseDelivery },
        { value: SiteDeliveryType.PackageDelivery, label: SITE_DELIVERY_TYPES_LABELS.PackageDelivery },
        {
            value: SiteDeliveryType.RefrigeratedTruckDelivery,
            label: SITE_DELIVERY_TYPES_LABELS.RefrigeratedTruckDelivery,
        },
    ];

    const contractTypesOptions = [
        { value: SiteContractType.Subscription, label: 'Abonnement' },
        { value: SiteContractType.FreeDeposit, label: 'Dépôt gratuit' },
    ];

    const selectedStateOption = stateOptions.find((option) => option.value === state);
    const selectedTypeOption = typesOptions.find((option) => option.value === type);
    const selectedDeliveryTypeOption = deliveryTypesOptions.find((option) => option.value === deliveryType);
    const selectedContractTypeOption = contractTypesOptions.find((option) => option.value === contractType);

    const mondayInThreeWeeks = new Date();
    mondayInThreeWeeks.setDate(mondayInThreeWeeks.getDate() + 21 - ((mondayInThreeWeeks.getDay() + 6) % 7));
    mondayInThreeWeeks.setHours(0, 0, 0, 0);

    return (
        <SectionContainer title="Informations Générales" isInitiallyOpen>
            <>
                <ColumnsSectionContainer numberOfColumns={2}>
                    <SectionColumn>
                        <CopyValue value={siteId} />
                        <DetailFormValue
                            label="Nom"
                            placeholder="Modifier le nom"
                            data-test="site-details_name"
                            {...register('name')}
                        />
                        <TotemLabel>QR Code</TotemLabel>
                        <QRCodeContainer>
                            <QRCodeCanvas value={qrCodeUrl} size={200} />
                        </QRCodeContainer>
                        <ExternalResource>
                            <ExternalResourceLink
                                href={getAvaLink(avaClientId, avaSiteId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                $disabled={!avaSiteId}
                            >
                                <TotemImage
                                    src="data:image/svg+xml,%3c?xml%20version=%271.0%27%20encoding=%27UTF-8%27?%3e%3csvg%20version=%271.1%27%20id=%27Layer_1%27%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%271063px%27%20height=%27540px%27%20style=%27background-color:%20white;%27%3e%3cg%20transform=%27translate(43,%200)%27%3e%3cpath%20d=%27M4.28450199,387.567034%20L135.551222,118.855464%20C141.812432,100.890629%20158.900832,88%20179,88%20C198.501996,88%20215.169473,100.136014%20221.862223,117.267832%20L352.908923,385.377915%20C356.781572,392.024331%20359,399.753095%20359,408%20C359,432.852814%20338.852814,453%20314,453%20C296.594365,453%20281.496786,443.118042%20274.011634,428.658499%20L273.934,428.698391%20L179.227886,234.511339%20L88.6295131,424.31597%20C81.7891833,441.13904%2065.2797294,453%2046,453%20C20.5949015,453%200,432.405098%200,407%20C0,400.056886%201.53824845,393.47305%204.29253714,387.570699%20Z%27%20id=%27Combined-Shape%27%20fill=%27%23A6BBCC%27%3e%3c/path%3e%3cpath%20d=%27M633.284502,387.567034%20L764.551222,118.855464%20C770.812432,100.890629%20787.900832,88%20808,88%20C827.49645,88%20844.159993,100.129112%20850.856511,117.253217%20L982.589287,386.588189%20C986.04028,392.955444%20988,400.248863%20988,408%20C988,432.852814%20967.852814,453%20943,453%20C923.292835,453%20906.544465,440.331898%20900.453933,422.694739%20L808.227886,234.511339%20L718.025276,423.308595%20C711.443356,440.663606%20694.662337,453%20675,453%20C649.594902,453%20629,432.405098%20629,407%20C629,400.056841%20630.538269,393.472963%20633.292592,387.570582%20Z%27%20id=%27Combined-Shape-Copy%27%20fill=%27%23A0A2A1%27%3e%3c/path%3e%3cpath%20d=%27M237.279652,437.68651%20L427.402701,46.6998626%20C436.341,19.5773943%20461.884227,0%20492,0%20C518.633855,0%20541.691376,15.3121062%20552.849681,37.6134367%20L552.928079,37.6171005%20L749.469527,439.177405%20C755.5191,449.197112%20759,460.942052%20759,473.5%20C759,510.226936%20729.226936,540%20692.5,540%20C664.366733,540%20640.313818,522.529937%20630.597932,497.846488%20L493.102913,216.970504%20L359.555225,496.232857%20C349.792483,521.822569%20325.019025,540%20296,540%20C258.444637,540%20228,509.555363%20228,472%20C228,459.484617%20231.381078,447.758927%20237.27965,437.686513%20Z%27%20id=%27Combined-Shape-Copy-2%27%20fill=%27%23004677%27%20transform=%27translate(493.500000,%20270.000000)%20scale(1,%20-1)%20translate(-493.500000,%20-270.000000)%20%27%3e%3c/path%3e%3c/g%3e%3c/svg%3e"
                                    alt="Logo Ava"
                                    size={30}
                                />
                            </ExternalResourceLink>
                            <DetailFormValue
                                type="number"
                                step="1"
                                defaultValue={avaSiteId}
                                width="160px"
                                {...register('avaSiteId', { required: false, valueAsNumber: true })}
                            />
                        </ExternalResource>
                        <DetailFormDatePicker
                            label="Date de lancement"
                            defaultValue={launchDate}
                            name="launchDate"
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) =>
                                date ? formatDateAsAnniversary({ dateTime: date, useNewFormat: false }) : null
                            }
                        />
                        <DetailFormDatePicker
                            label="Date de fermeture définitive"
                            sublabel="Les produits vont être récupérés lors de la dernière livraison à cette date. Veuillez mettre un jour où nous sommes sûr de livrer."
                            defaultValue={removalDate}
                            name="removalDate"
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value, true) : null)}
                            transformDateToValue={(date) =>
                                date ? formatDateAsAnniversary({ dateTime: date, useNewFormat: true }) : null
                            }
                        />
                        <DetailFormCheckbox
                            name="shouldOperateOnTotops"
                            label="Opérer sur le site"
                            sublabel="A décocher si le site n'est pas opéré sur la TotAdmin/Totops"
                            checked={shouldOperateOnTotops}
                            onChange={() => setValue('shouldOperateOnTotops', !shouldOperateOnTotops)}
                        />
                        <DetailFormCheckbox
                            name="shouldAutomaticallyCreateTargetReassortment"
                            label="Création automatique du réassort par stock cible"
                            sublabel="Permet de générer automatiquement le réassort par stock cible (Dry & Fruit) chaque jour."
                            checked={shouldAutomaticallyCreateTargetReassortment}
                            onChange={() =>
                                setValue(
                                    'shouldAutomaticallyCreateTargetReassortment',
                                    !shouldAutomaticallyCreateTargetReassortment,
                                )
                            }
                        />
                        <DetailFormCheckbox
                            name="shouldAutomaticallyCreateManualReassortment"
                            label="Création automatique du réassort manuel"
                            sublabel="Permet de générer automatiquement le réassort manuel (Menus Fresh) chaque jour."
                            checked={shouldAutomaticallyCreateManualReassortment}
                            onChange={() =>
                                setValue(
                                    'shouldAutomaticallyCreateManualReassortment',
                                    !shouldAutomaticallyCreateManualReassortment,
                                )
                            }
                        />
                        <DetailFormCheckbox
                            name="shouldAutomaticallyFinishSiteSetup"
                            label="Fin de rangement automatique"
                            sublabel="Permet de forcer le rangement s'il n'est pas terminé lorsque la livraison est marquée comme livrée"
                            checked={shouldAutomaticallyFinishSiteSetup}
                            onChange={() =>
                                setValue('shouldAutomaticallyFinishSiteSetup', !shouldAutomaticallyFinishSiteSetup)
                            }
                        />
                        <DetailFormCheckbox
                            name="shouldHaveTheftNotice"
                            label="Mettre des écriteaux sur le site"
                            sublabel="Demande au livreur de mettre des écriteaux anti démarques sur le site"
                            checked={shouldHaveTheftNotice}
                            onChange={() => setValue('shouldHaveTheftNotice', !shouldHaveTheftNotice)}
                        />
                        <DetailFormCheckbox
                            name="hasExternalSetupProcess"
                            label="Rangement sur site externe (pas de totops)"
                            sublabel="A cocher si le rangement du site est fait par un process externe (pas de totops)"
                            checked={hasExternalSetupProcess}
                            onChange={() => setValue('hasExternalSetupProcess', !hasExternalSetupProcess)}
                        />
                        <DetailFormCheckbox
                            name="shouldModulateForExpired"
                            label="Moduler les stocks cibles en fonction des pertes expirés"
                            sublabel="Permet de moduler les stocks cibles en fonction des pertes expirés"
                            checked={shouldModulateForExpired}
                            onChange={() => setValue('shouldModulateForExpired', !shouldModulateForExpired)}
                        />
                        <DetailFormCheckbox
                            name="shouldModulateForUnknownLosses"
                            label="Moduler les stocks cibles en fonction des démarques inconnues"
                            sublabel="Permet de moduler les stocks cibles en fonction des démarques inconnues"
                            checked={shouldModulateForUnknownLosses}
                            onChange={() => setValue('shouldModulateForUnknownLosses', !shouldModulateForUnknownLosses)}
                        />
                    </SectionColumn>
                    <SectionColumn>
                        <CopyValue
                            label="Dashboard Metabase"
                            value={
                                metabaseDashboardData?.siteMetabaseDashboardUrl ??
                                (metabaseDashboardError ? 'indisponible' : 'chargement')
                            }
                        />
                        <DetailFormSelect<SiteType>
                            label="Type"
                            placeholder="Sélectionner un type"
                            name="type"
                            defaultValue={selectedTypeOption?.value}
                            options={typesOptions}
                        />
                        <DetailFormSelect<SiteDeliveryType>
                            label="Type de livraison"
                            placeholder="Sélectionner un type de livraison"
                            name="deliveryType"
                            defaultValue={selectedDeliveryTypeOption?.value}
                            options={deliveryTypesOptions}
                        />
                        <DetailFormSelect<SiteContractType>
                            label="Type de contrat"
                            placeholder="Sélectionner un type de contrat"
                            name="contractType"
                            defaultValue={selectedContractTypeOption?.value}
                            options={contractTypesOptions}
                        />
                        <DetailFormCheckbox
                            name="isSecured"
                            label="Site Sécurisé (Sensei, Nu, Digitiz, Jofemar, Vending, ...)"
                            sublabel="A cocher si le site n'est pas à la confiance"
                            checked={isSecured}
                            onChange={() => {
                                setValue('isSecured', !isSecured);
                            }}
                        />
                        <>
                            <DetailFormCheckbox
                                name="hasCoffee"
                                label="Activer l'onglet Café sur la TotMobile"
                                sublabel="Permet d'activer l'onglet 'Café' sur l'application si nécessaire (masqué sur un site non sécurisé avec le payment Nayax legacy)"
                                checked={hasCoffee}
                                onChange={() => {
                                    setValue('hasCoffee', !hasCoffee);
                                }}
                            />
                            <DetailFormCheckbox
                                name="hasSensei"
                                label="Site avec des armoires Sensei"
                                sublabel="Permet d'ajouter le QRCode Sensei sur la TotMobile"
                                checked={hasSensei}
                                onChange={() => {
                                    setValue('hasSensei', !hasSensei);
                                }}
                            />
                            <DetailFormCheckbox
                                name="isOpenOnWeekend"
                                label="Site ouvert le weekend"
                                sublabel="Permet de retirer les produits qui expirent J-1 avant la prochaine livraison, weekend inclus"
                                checked={isOpenOnWeekend}
                                onChange={() => {
                                    setValue('isOpenOnWeekend', !isOpenOnWeekend);
                                }}
                            />
                            <DetailFormCheckbox
                                name="useNayaxCard"
                                label="Permettre le paiement avec une carte privative Nayax"
                                sublabel="Permet d'ajouter le QRCode Nayax sur la TotMobile"
                                checked={useNayaxCard}
                                onChange={() => {
                                    setValue('useNayaxCard', !useNayaxCard);
                                }}
                            />
                        </>
                        <OrganizationSelect
                            label="Organisation"
                            value={watchedOrganizationId}
                            onChange={(newOrganizationId) => setValue('organizationId', newOrganizationId)}
                            isClearable={false}
                            dataTest="site-details_organization-select"
                        />
                        <DetailValue label="Totem" value={`#${totemNumber}`} />
                        <DetailFormValue
                            label="Estimation du nombre d'utilisateurs sur site"
                            defaultValue={estimatedUserCount}
                            type="number"
                            step="1"
                            data-test="site-details_estimated-user-count"
                            {...register('estimatedUserCount', { valueAsNumber: true, min: 0 })}
                        />
                        <DetailFormValue
                            label="Facturer la démarque inconnue à partir de (0€ = on facture toute la DI)"
                            defaultValue={unknownShrinkageBillingThreshold}
                            type="number"
                            step="1"
                            data-test="site-details_bill-unknown-shrinkage"
                            {...register('unknownShrinkageBillingThreshold', {
                                valueAsNumber: true,
                                min: 0,
                                required: false,
                            })}
                        />
                        <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                        <DetailValue label="Dernière mise à jour" value={getExtendedFormattedDatetime(updatedAt)} />
                        <DetailFormSelect<SiteState>
                            label="Statut"
                            options={stateOptions}
                            defaultValue={selectedStateOption?.value}
                            placeholder="Modifier la valeur"
                            name="state"
                        />
                        <StateHistory label="Historique de statuts" records={formattedStateHistory} />
                    </SectionColumn>
                </ColumnsSectionContainer>
            </>
        </SectionContainer>
    );
};

const QRCodeContainer = styled.div`
    padding: 10px;
    background-color: white;
    height: 220px;
    width: 220px;
`;

const ExternalResource = styled.div`
    display: flex;
    flex-direction: row;

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;

const ExternalResourceLink = styled.a<{ $disabled: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;

    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'default')};
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

    &:hover {
        text-decoration: underline;
    }

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
